/**
 * Which versions of a document a resolution may name.
 *
 * A suggestion closes against a version created after it was raised. A finding
 * with an assessment stamp closes against a version carrying content the
 * assessment did not read (`versionsAfterAssessment`) — the dialog used to
 * list every version, the assessed one first, so a finding could be "resolved"
 * by the very text it was raised about. That case is real (the text already
 * covered it), so it has its own path: the current version, with a note saying
 * how. The server holds the same rule (`judgeFindingResolution`). A finding
 * with no stamp has no baseline, so every version is offered, as before.
 *
 * @module components/regulation/policy/resolutionCandidates
 */
import { findingAssessedBaseline, versionsAfterAssessment } from "@repo/shared/findingStaleness";

/** The shortest note that says how a version already covers a finding. */
export const MIN_COVERED_NOTE_LENGTH = 5;

export interface VersionCandidate {
  readonly key: string;
  readonly version: number;
  readonly createdAt: string;
  readonly note: string | null;
  /** File versions and text captures number independently. */
  readonly label: "file" | "text";
}

/** What the finding was assessed against; absent for a suggestion. */
export interface AssessedStamp {
  readonly assessedVersion?: number | null;
  readonly assessedRevision?: number | null;
}

/** Whether the finding's assessment was stamped, so "newer" has a meaning. */
export const assessmentBaselineKnown = (assessed: AssessedStamp | null | undefined): boolean =>
  assessed != null && findingAssessedBaseline(assessed) !== null;

/**
 * The candidates a resolution offers, newest first. A suggestion: those created
 * after it was raised. A finding: those carrying content its assessment did not
 * read when it was stamped, and every version when it was not.
 */
export function resolutionCandidates(
  candidates: ReadonlyArray<VersionCandidate>,
  input:
    | { readonly kind: "suggestion"; readonly raisedAt: string }
    | { readonly kind: "finding"; readonly assessed: AssessedStamp | null | undefined },
): VersionCandidate[] {
  const newestFirst = (items: ReadonlyArray<VersionCandidate>) =>
    [...items].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
  if (input.kind === "suggestion") {
    const raised = Date.parse(input.raisedAt);
    return newestFirst(candidates.filter((candidate) => Date.parse(candidate.createdAt) > raised));
  }
  const baseline = input.assessed ? findingAssessedBaseline(input.assessed) : null;
  if (baseline === null) return newestFirst(candidates);
  const later = new Set(
    versionsAfterAssessment(
      candidates.map((candidate) => ({ ...candidate, kind: candidate.label })),
      baseline,
    ).map((candidate) => candidate.key),
  );
  return newestFirst(candidates.filter((candidate) => later.has(candidate.key)));
}

/** Why the "already covered" note cannot be saved yet, or null when it can. */
export function coveredNoteProblem(note: string): string | null {
  const missing = MIN_COVERED_NOTE_LENGTH - note.trim().length;
  return missing > 0
    ? `Say how the current version already covers this finding (${missing} more ${missing === 1 ? "character" : "characters"}).`
    : null;
}
